/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, RestSettings } from '@gh/core-data';
import { Observable } from 'rxjs';
import {
  AverageBuildTimeDto,
  AverageContractPriceDto,
  AverageHouseCostChangeDto,
  AveragePreconstructionBuildTimesDto,
  AverageTimeFromSfToDepositDto,
  ConversionRatesDto,
  DashboardWidgetsType,
  GrossAndNetProfitDto,
  GrossProfitAndOverheadsDto,
  MaintenanceTasksOverdueDto,
  MonthlySalesSlabsDto,
  PreconstructionBuildTimesDto,
  PriorityAndStageDto,
  SalesLeadsBudgetsDto,
  SalespersonTotalDto,
  TopCustomersByEarliestContactDto,
  TopCustomersByPriorityDto,
  WidgetsDto,
} from './dto';
import {
  AverageBuildTimeMapper,
  AverageContractPriceMapper,
  AverageHouseCostChangeMapper,
  AveragePreconstructionBuildTimesMapper,
  AverageTimeFromSfToDepositMapper,
  ConversionRatesMapper,
  GrossAndNetProfitMapper,
  GrossProfitAndOverheadsMapper,
  MaintenanceTasksOverdueMapper,
  MonthlySalesSlabsMapper,
  PreconstructionBuildTimesMapper,
  PriorityAndStageMapper,
  SalesLeadsBudgetsMapper,
  SalespersonTotalMapper,
  TopCustomersByEarliestContactMapper,
  TopCustomersByPriorityMapper,
  WidgetsMapper,
} from './mappers';

@Injectable()
export class DashboardsRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  getAverageBuildTime(): Observable<AverageBuildTimeDto> {
    return this.httpGet(`${this.bp}/dashboards/average-build-time`, AverageBuildTimeMapper);
  }

  getAverageContractPrice(): Observable<AverageContractPriceDto> {
    return this.httpGet(`${this.bp}/dashboards/average-contract-price`, AverageContractPriceMapper);
  }

  getAverageHouseCostChange(): Observable<AverageHouseCostChangeDto> {
    return this.httpGet(`${this.bp}/dashboards/average-house-cost-change`, AverageHouseCostChangeMapper);
  }

  getGrossAndNetProfit(): Observable<GrossAndNetProfitDto> {
    return this.httpGet(`${this.bp}/dashboards/gross-and-net-profit`, GrossAndNetProfitMapper);
  }

  getGrossProfitAndOverheads(officeId?: ID): Observable<GrossProfitAndOverheadsDto> {
    return this.httpGet(
      `${this.bp}/dashboards/gross-profit-and-overheads`,
      { officeId },
      void 0,
      GrossProfitAndOverheadsMapper,
    );
  }

  getAverageTimeFromSfToDeposit(): Observable<AverageTimeFromSfToDepositDto> {
    return this.httpGet(`${this.bp}/dashboards/sales/average-time-from-sf-to-deposit`, AverageTimeFromSfToDepositMapper);
  }

  getConversionRates(): Observable<ConversionRatesDto> {
    return this.httpGet(`${this.bp}/dashboards/sales/conversion-rates`, ConversionRatesMapper);
  }

  getAveragePreconstructionBuildTimes(): Observable<AveragePreconstructionBuildTimesDto> {
    return this.httpGet(`${this.bp}/dashboards/average-preconstruction-build-times`, AveragePreconstructionBuildTimesMapper);
  }

  getMaintenanceTasksOverdue(): Observable<MaintenanceTasksOverdueDto[]> {
    return this.httpGet(`${this.bp}/dashboards/maintenance-tasks-overdue`, arrayOf(MaintenanceTasksOverdueMapper));
  }

  getMonthlySalesSlabs(): Observable<MonthlySalesSlabsDto> {
    return this.httpGet(`${this.bp}/dashboards/monthly-sales-slabs`, MonthlySalesSlabsMapper);
  }

  getPreconstructionBuildTimes(): Observable<PreconstructionBuildTimesDto> {
    return this.httpGet(`${this.bp}/dashboards/preconstruction-build-times`, PreconstructionBuildTimesMapper);
  }

  getPriorityAndStage(): Observable<PriorityAndStageDto> {
    return this.httpGet(`${this.bp}/dashboards/sales/priority-and-stage`, PriorityAndStageMapper);
  }

  getSalesLeadsBudgets(): Observable<SalesLeadsBudgetsDto> {
    return this.httpGet(`${this.bp}/dashboards/sales-leads-budgets`, SalesLeadsBudgetsMapper);
  }

  getSalesLeadsBudgetsForSalesperson(): Observable<SalesLeadsBudgetsDto> {
    return this.httpGet(`${this.bp}/dashboards/sales/sales-leads-budgets`, SalesLeadsBudgetsMapper);
  }

  getSalespersonTotal(): Observable<SalespersonTotalDto> {
    return this.httpGet(`${this.bp}/dashboards/salesperson-total`, SalespersonTotalMapper);
  }

  getTopCustomersByEarliestContact(): Observable<TopCustomersByEarliestContactDto[]> {
    return this.httpGet(
      `${this.bp}/dashboards/sales/top-customers-by-earliest-contact`,
      arrayOf(TopCustomersByEarliestContactMapper),
    );
  }

  getTopCustomersByPriority(): Observable<TopCustomersByPriorityDto[]> {
    return this.httpGet(`${this.bp}/dashboards/sales/top-customers-by-priority`, arrayOf(TopCustomersByPriorityMapper));
  }

  getWidgets(type: DashboardWidgetsType): Observable<WidgetsDto[]> {
    return this.httpGet(`${this.bp}/widgets?type=${type}`, arrayOf(WidgetsMapper));
  }

  updateWidgets(type: DashboardWidgetsType, widgets: WidgetsDto[]): Observable<WidgetsDto[]> {
    return this.httpPut(`${this.bp}/widgets?type=${type}`, widgets, arrayOf(WidgetsMapper));
  }
}
